import React, { useState } from 'react';
import { Card, Form, Input, Select, Button, Space, Tag, Upload, message, Typography, Switch } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, InboxOutlined } from '@ant-design/icons';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { feedbackService, FeedbackCategory, FeedbackMediaItem } from '../../services/feedback/feedbackService';
import { useAuth } from '../../contexts/AuthContext';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';

const { TextArea } = Input;
const { Option } = Select;
const { Paragraph } = Typography;

const FeedbackPostForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { isDemoBrowseOnly } = useAuth();
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [media, setMedia] = useState<FeedbackMediaItem[]>([]);

  const handleAddTag = () => {
    const value = tagInput.trim();
    if (value && !tags.includes(value)) {
      setTags([...tags, value]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleUpload = async ({ file, onSuccess, onError }: any) => {
    try {
      setUploading(true);
      const items = await feedbackService.uploadMedia([file as File]);
      setMedia((prev) => [...prev, ...items]);
      onSuccess(null, file);
      message.success('上传成功');
    } catch (error: any) {
      console.error(error);
      onError(error);
      message.error(error.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveMedia = (item: FeedbackMediaItem) => {
    setMedia((prev) => prev.filter((m) => m.key !== item.key));
  };

  const handleSubmit = async (values: any) => {
    try {
      if (isDemoBrowseOnly) {
        message.info('演示模式：反馈未提交服务器，可返回广场查看示例帖子。');
        navigate('/feedback/square');
        return;
      }
      const payload = {
        title: values.title,
        content: values.content,
        category: values.category as FeedbackCategory,
        tags,
        media,
        allowComment: values.allowComment,
        adminOnlyReply: values.adminOnlyReply,
      };
      await feedbackService.createPost(payload);
      message.success('匿名反馈发布成功');
      navigate('/feedback/square');
    } catch (error: any) {
      message.error(error.message || '发布失败');
    }
  };

  return (
    <div className="page-container full-height members-lims fb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <Link to="/feedback/square" className="fb-crumb-link">
          畅所欲言
        </Link>
        <span className="mm-crumb-sep">/</span>
        <span aria-current="page">发表反馈</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">发表匿名反馈</h1>
          <p className="mm-subtitle mb-0">
            对普通成员完全匿名；合规场景下仅少数管理员可按规定追溯。图片与视频上传时会清除设备与位置元数据。
          </p>
        </div>
      </header>

      <div className="fb-form-panel">
        <Card bordered={false} className="fb-form-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              category: '建议',
              allowComment: true,
              adminOnlyReply: false,
            }}
          >
            <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
              <Input maxLength={100} placeholder="例如：建议优化离心机预约规则" />
            </Form.Item>

            <Form.Item name="content" label="详细内容" rules={[{ required: true, message: '请输入详细内容' }]}>
              <TextArea
                rows={6}
                maxLength={2000}
                showCount
                placeholder="请尽量聚焦于具体现象、影响和建议方案，避免出现可直接识别个人身份的细节。"
              />
            </Form.Item>

            <Form.Item name="category" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
              <Select style={{ maxWidth: 320 }}>
                <Option value="建议">建议</Option>
                <Option value="吐槽/反馈">吐槽 / 反馈</Option>
                <Option value="问题举报">问题举报</Option>
                <Option value="求助">求助</Option>
                <Option value="投票讨论">投票讨论</Option>
                <Option value="表扬">表扬</Option>
              </Select>
            </Form.Item>

            <Form.Item label="标签（可选）">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space wrap>
                  {tags.map((tag) => (
                    <Tag key={tag} closable onClose={() => handleRemoveTag(tag)}>
                      {tag}
                    </Tag>
                  ))}
                </Space>
                <Space>
                  <Input
                    style={{ width: 280 }}
                    placeholder="输入标签后回车添加"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onPressEnter={handleAddTag}
                  />
                  <Button onClick={handleAddTag}>添加标签</Button>
                </Space>
              </Space>
            </Form.Item>

            <Form.Item label="图片 / 视频（可选）">
              <Upload.Dragger
                name="file"
                multiple
                customRequest={handleUpload}
                showUploadList={false}
                accept="image/*,video/*"
                disabled={uploading}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
                <p className="ant-upload-hint">
                  支持图片与短视频；单图 ≤10MB，单视频 ≤200MB。上传后会清除图片元数据并重命名。
                </p>
              </Upload.Dragger>

              {media.length > 0 && (
                <Space direction="vertical" style={{ marginTop: 16, width: '100%' }}>
                  <Paragraph strong style={{ marginBottom: 0 }}>
                    已上传的媒体
                  </Paragraph>
                  <Space wrap>
                    {media.map((item) => (
                      <Tag key={item.key} closable onClose={() => handleRemoveMedia(item)} color={item.type === 'image' ? 'blue' : 'purple'}>
                        {item.type === 'image' ? '图片' : '视频'} · {item.fileName}
                      </Tag>
                    ))}
                  </Space>
                </Space>
              )}
            </Form.Item>

            <Form.Item label="讨论设置">
              <Space direction="vertical">
                <Space>
                  <Form.Item name="allowComment" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                  <span>允许其他成员评论</span>
                </Space>
                <Space>
                  <Form.Item name="adminOnlyReply" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                  <span>仅允许管理员/负责人回复</span>
                </Space>
              </Space>
            </Form.Item>

            <Form.Item className="mb-0">
              <div className="fb-form-actions">
                <Space wrap>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={uploading}>
                    发布匿名反馈
                  </Button>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/feedback/square')}>
                    返回广场
                  </Button>
                </Space>
              </div>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default FeedbackPostForm;
